import { Invoice } from "../../../pojo/Pricing";
import { S25Const } from "../../../util/s25-const";
import { IPaymentDetails } from "../s25-payments/payment.service";
import { invoiceDisplayName } from "../reference-set-pricing/s25.event.pricing.store";
import { isReferenceSet } from "../reference-set-pricing/invoice/system-sets/system-sets";
import { InvoiceTotals } from "../reference-set-pricing/invoice/totals/invoice-totals";
import { AccountSummaryMetrics } from "../reference-set-pricing/account-summary/account-summary-metrics";

/** An organization's whole invoice, not one event's share of it. */
const WHOLE_INVOICE: InvoiceTotals.InvoiceScope = { combineRelated: true, eventId: undefined };

function orgPayments(invoice: Invoice, orgId: number): IPaymentDetails[] {
    return (invoice.payments ?? []).filter((payment) => payment.organizationId === orgId && !payment.isVoid);
}

function eventName(eventId: number, eventNameById: ReadonlyMap<number, string>): string {
    if (!eventId) return "—";
    return eventNameById.get(eventId) ?? S25Const.private;
}

export namespace OrgInvoiceRecords {
    export type Status = "paid" | "outstanding" | "overdue";

    /**
     * The event an invoice bills for, or 0 for one that names none. Only a pricing sheet carries
     * `billDefn`, so everything else has to be read off the per-event subtotals, which name the
     * event they totalled.
     */
    export function eventIdOf(invoice: Invoice): number {
        return (
            invoice.billDefn?.events?.[0] ||
            invoice.billing?.subtotals?.[0]?.accountEvent?.find((subtotal) => subtotal.eventId)?.eventId ||
            0
        );
    }

    export type Record = {
        id: number;
        invoiceNumber: string;
        invoiceName: string;
        eventId: number;
        eventName: string;
        /** The invoice's LATEST due date, per the ticket - not every milestone on it. */
        dueDateISO: string;
        /**
         * The latest due date still owed, which is what decides whether the invoice is late. Empty
         * when every payment on it has settled: a balance left over from an invoice nobody is
         * currently being chased for is outstanding, not overdue.
         */
        unpaidDueDateISO: string;
        totalCharge: number;
        paidAmount: number;
        outstandingPaymentIds: number[];
    };

    /**
     * The organization's rows, one per invoice it is charged on or has paid against. Reference sets only
     * record what the other invoices bill, so counting them would double every figure.
     */
    export function from(invoices: Invoice[], orgId: number, eventNameById: ReadonlyMap<number, string>): Record[] {
        const records: Record[] = [];

        for (const invoice of invoices) {
            if (isReferenceSet(invoice)) continue;

            const payments = orgPayments(invoice, orgId);
            const figures = InvoiceTotals.orgFiguresByOrg(invoice, [orgId], WHOLE_INVOICE).get(orgId);
            if (!figures?.charge && !payments.length) continue;

            const outstanding = payments
                .filter((payment) => !AccountSummaryMetrics.isPaid(payment))
                .sort((first, second) => (second.dueDate ?? "").localeCompare(first.dueDate ?? ""));
            const eventId = eventIdOf(invoice);
            const dueDates = payments
                .map((payment) => payment.dueDate)
                .filter((dueDate): dueDate is string => !!dueDate);

            records.push({
                id: invoice.id,
                invoiceNumber: InvoiceTotals.invoiceNumbersByOrg(invoice).get(orgId) || "",
                invoiceName: invoiceDisplayName(invoice),
                eventId,
                eventName: eventName(eventId, eventNameById),
                dueDateISO: dueDates.sort().at(-1) ?? "",
                unpaidDueDateISO: outstanding[0]?.dueDate ?? "",
                totalCharge: figures?.charge ?? 0,
                paidAmount: figures?.paid ?? 0,
                outstandingPaymentIds: outstanding.map((payment) => payment.paymentDetailId),
            });
        }

        return records;
    }

    /**
     * Most recently billed first. An invoice with no payments has no due date to be recent against,
     * so it sorts below every invoice that has one rather than above them all.
     */
    export function byDueDateDesc(first: Record, second: Record): number {
        if (!first.dueDateISO || !second.dueDateISO) return (second.dueDateISO ? 1 : 0) - (first.dueDateISO ? 1 : 0);
        return second.dueDateISO.localeCompare(first.dueDateISO);
    }
}
